import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Search, Filter, Plus, Minus, Save, AlertCircle, CheckCircle, ArrowRight, ArrowLeft } from 'lucide-react';
import { API_ENDPOINTS } from '../config/apiRouting';
import { useScope } from '../contexts/ScopeContext';

interface MenuItem {
  menuItemId: string;
  id?: string;
  name: string;
  description: string;
  category: string;
  subcategory: string;
  basePrice: number;
  price?: number;
  imageUrl?: string;
  isActive: boolean;
  isAvailable: boolean;
  enterpriseId: string;
  conceptId?: string;
  franchiseeId?: string;
  storeId?: string;
  tags: string[];
}

interface Enterprise {
  enterpriseId: string;
  name: string;
}

interface Concept {
  conceptId: string;
  name: string;
  enterpriseId: string;
}

interface Franchisee {
  franchiseeId: string;
  name: string;
  enterpriseId: string;
}

interface Store {
  storeId: string;
  name: string;
  storeCode?: string;
  conceptId?: string;
  franchiseeId?: string;
  enterpriseId?: string;
  status: string;
}

interface AssignmentLevel {
  type: 'enterprise' | 'concept' | 'franchisee' | 'store';
  id: string;
  name: string;
  displayName: string;
}

interface MenuItemAssignmentProps {
  user: any;
}

const MenuItemCard: React.FC<{
  item: MenuItem;
  isAssigned: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onToggle: () => void;
}> = ({ item, isAssigned, isSelected, onSelect, onToggle }) => {
  return (
    <div
      className={`p-4 border rounded-lg cursor-pointer transition-all ${
        isSelected
          ? 'border-blue-500 bg-blue-50'
          : isAssigned
          ? 'border-green-200 bg-green-50 hover:bg-green-100'
          : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
      }`}
      onClick={onSelect}
    >
      <div className="flex items-start space-x-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onSelect}
          className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          onClick={(e) => e.stopPropagation()}
        />
        
        {item.imageUrl && (
          <img
            src={item.imageUrl}
            alt={item.name}
            className="w-12 h-12 object-cover rounded-md flex-shrink-0"
          />
        )}
        
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">
            {item.name}
          </h4>
          <p className="text-xs text-gray-500 truncate">
            {item.description}
          </p>
          <div className="flex items-center space-x-2 mt-1">
            <span className={`text-xs px-2 py-1 rounded ${
              isAssigned 
                ? 'bg-green-100 text-green-700' 
                : 'bg-gray-100 text-gray-700'
            }`}>
              {item.category}
            </span>
            <span className="text-xs font-medium text-green-600">
              ${item.basePrice || item.price || 0}
            </span>
            {isAssigned && <CheckCircle className="h-3 w-3 text-green-600" />}
          </div>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className={`p-1 rounded-md ${
            isAssigned
              ? 'text-red-600 hover:bg-red-100'
              : 'text-green-600 hover:bg-green-100'
          }`}
          title={isAssigned ? 'Remove assignment' : 'Assign item'}
        >
          {isAssigned ? <ArrowLeft className="h-4 w-4" /> : <ArrowRight className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
};

const MenuItemAssignment: React.FC<MenuItemAssignmentProps> = ({ user }) => {
  // State management
  const [menuItems, setMenuItems] = useState<MenuItem[]>([]);
  // Snapshot of assignment fields at load time; used to diff on save so we only PUT changed items.
  const [originalAssignments, setOriginalAssignments] = useState<Map<string, { conceptId: string; franchiseeId: string; storeId: string }>>(new Map());
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [concepts, setConcepts] = useState<Concept[]>([]);
  const [franchisees, setFranchisees] = useState<Franchisee[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  
  // Assignment state
  const { enterpriseId: scopeEnterpriseId } = useScope();
  const [selectedEnterprise, setSelectedEnterprise] = useState<string>('');
  const [userPickedEnterprise, setUserPickedEnterprise] = useState(false);

  // Follow the header enterprise selector until the user picks locally.
  useEffect(() => {
    if (userPickedEnterprise || !scopeEnterpriseId) return;
    if (selectedEnterprise === scopeEnterpriseId) return;
    if (enterprises.length > 0 && !enterprises.some((e) => e.enterpriseId === scopeEnterpriseId)) return;
    setSelectedEnterprise(scopeEnterpriseId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scopeEnterpriseId, userPickedEnterprise, enterprises]);
  const [assignmentLevel, setAssignmentLevel] = useState<AssignmentLevel | null>(null);
  const [availableItems, setAvailableItems] = useState<MenuItem[]>([]);
  const [assignedItems, setAssignedItems] = useState<MenuItem[]>([]);
  
  // Selection state
  const [selectedAvailable, setSelectedAvailable] = useState<Set<string>>(new Set());
  const [selectedAssigned, setSelectedAssigned] = useState<Set<string>>(new Set());
  
  // UI state
  const [searchAvailable, setSearchAvailable] = useState('');
  const [searchAssigned, setSearchAssigned] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);

  // Load initial data
  useEffect(() => {
    loadEnterprises();
  }, []);

  useEffect(() => {
    if (selectedEnterprise) {
      loadEnterpriseData(selectedEnterprise);
    }
  }, [selectedEnterprise]);

  useEffect(() => {
    updateAssignmentLists();
  }, [menuItems, assignmentLevel]);

  const loadEnterprises = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.enterprises());
      const data = await response.json();
      console.log('Enterprises API response:', data);
      
      // Handle both direct array response and wrapped response
      let enterpriseList = [];
      if (Array.isArray(data)) {
        // Direct array response
        enterpriseList = data;
      } else if (data.success && data.data) {
        // Wrapped response with success flag
        enterpriseList = data.data;
      } else if (data.data && Array.isArray(data.data)) {
        // Wrapped response without success flag
        enterpriseList = data.data;
      }
      
      setEnterprises(enterpriseList);
      console.log('Enterprises loaded:', enterpriseList);
      
      if (enterpriseList.length > 0) {
        // Prefer header scope if present and in the list; fall back to first available.
        const preferred = scopeEnterpriseId && enterpriseList.some((e: Enterprise) => e.enterpriseId === scopeEnterpriseId)
          ? scopeEnterpriseId
          : enterpriseList[0].enterpriseId;
        setSelectedEnterprise(preferred);
      } else {
        // No enterprises found, stop loading
        setLoading(false);
      }
    } catch (error) {
      console.error('Error loading enterprises:', error);
      setLoading(false);
    }
  };

  const loadEnterpriseData = async (enterpriseId: string) => {
    try {
      setLoading(true);
      
      // Load all data in parallel
      const [menuResponse, conceptsResponse, franchiseesResponse, storesResponse] = await Promise.all([
        fetch(API_ENDPOINTS.menuItems(enterpriseId)),
        fetch(API_ENDPOINTS.concepts(enterpriseId)),
        fetch(API_ENDPOINTS.franchisees(enterpriseId)),
        fetch(API_ENDPOINTS.stores())
      ]);

      const [menuData, conceptsData, franchiseesData, storesData] = await Promise.all([
        menuResponse.json(),
        conceptsResponse.json(),
        franchiseesResponse.json(),
        storesResponse.json()
      ]);

      // Handle menu items - support both wrapped and direct responses
      const menuItemsList = Array.isArray(menuData) ? menuData : (menuData.data || []);
      setMenuItems(menuItemsList);
      const snapshot = new Map<string, { conceptId: string; franchiseeId: string; storeId: string }>();
      menuItemsList.forEach((mi: MenuItem) => {
        snapshot.set(mi.menuItemId, {
          conceptId: mi.conceptId || '',
          franchiseeId: mi.franchiseeId || '',
          storeId: mi.storeId || ''
        });
      });
      setOriginalAssignments(snapshot);

      // Handle concepts - support both wrapped and direct responses
      const conceptsList = Array.isArray(conceptsData) ? conceptsData : (conceptsData.data || []);
      setConcepts(conceptsList);

      // Handle franchisees - support both wrapped and direct responses
      const franchiseesList = Array.isArray(franchiseesData) ? franchiseesData : (franchiseesData.data || []);
      setFranchisees(franchiseesList);

      // Handle stores - support both wrapped and direct responses
      const storesList = Array.isArray(storesData) ? storesData : (storesData.data || []);
      
      // Filter stores for this enterprise
      const enterpriseStores = storesList.filter((store: Store) => {
          // Primary filtering: check if store's concept or franchisee belongs to this enterprise
          const conceptMatch = conceptsList.some((concept: Concept) => concept.conceptId === store.conceptId);
          const franchiseeMatch = franchiseesList.some((franchisee: Franchisee) => franchisee.franchiseeId === store.franchiseeId);
          
          // Fallback filtering: check if store directly belongs to this enterprise
          const directEnterpriseMatch = store.enterpriseId === enterpriseId;
          
          return conceptMatch || franchiseeMatch || directEnterpriseMatch;
        });
        
        console.log('Store filtering debug:', {
          totalStores: storesList.length || 0,
          enterpriseConcepts: conceptsList.map((c: Concept) => c.conceptId),
          enterpriseFranchisees: franchiseesList.map((f: Franchisee) => f.franchiseeId),
          enterpriseId,
          filteredStores: enterpriseStores.length,
          storeDetails: enterpriseStores.map((s: Store) => ({
            storeId: s.storeId,
            name: s.name,
            conceptId: s.conceptId,
            franchiseeId: s.franchiseeId,
            enterpriseId: s.enterpriseId
          }))
        });
        
        setStores(enterpriseStores);

    } catch (error) {
      console.error('Error loading enterprise data:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateAssignmentLists = useCallback(() => {
    if (!assignmentLevel) {
      setAvailableItems(menuItems);
      setAssignedItems([]);
      return;
    }

    const assigned: MenuItem[] = [];
    const available: MenuItem[] = [];

    menuItems.forEach(item => {
      const isAssigned = isItemAssignedToLevel(item, assignmentLevel);
      
      if (isAssigned) {
        assigned.push(item);
      } else {
        available.push(item);
      }
    });

    setAssignedItems(assigned);
    setAvailableItems(available);
    
    // Clear selections when assignment level changes
    setSelectedAvailable(new Set());
    setSelectedAssigned(new Set());
  }, [menuItems, assignmentLevel]);

  const isItemAssignedToLevel = (item: MenuItem, level: AssignmentLevel): boolean => {
    switch (level.type) {
      case 'enterprise':
        return item.enterpriseId === level.id;
      case 'concept':
        return item.conceptId === level.id;
      case 'franchisee':
        return item.franchiseeId === level.id;
      case 'store':
        return item.storeId === level.id;
      default:
        return false;
    }
  };

  const toggleItemAssignment = (item: MenuItem, assign: boolean) => {
    if (!assignmentLevel) return;

    const updatedItem = { ...item };
    
    if (assign) {
      // Assign item to the selected level
      switch (assignmentLevel.type) {
        case 'concept':
          updatedItem.conceptId = assignmentLevel.id;
          break;
        case 'franchisee':
          updatedItem.franchiseeId = assignmentLevel.id;
          break;
        case 'store':
          updatedItem.storeId = assignmentLevel.id;
          break;
      }
    } else {
      // Unassign item from the selected level
      switch (assignmentLevel.type) {
        case 'concept':
          updatedItem.conceptId = '';
          break;
        case 'franchisee':
          updatedItem.franchiseeId = '';
          break;
        case 'store':
          updatedItem.storeId = '';
          break;
      }
    }

    // Update menu items state
    setMenuItems(prev => prev.map((mi: MenuItem) => 
      mi.menuItemId === item.menuItemId ? updatedItem : mi
    ));
    
    setHasChanges(true);
  };

  const handleBulkAssign = (items: MenuItem[], assign: boolean) => {
    if (!assignmentLevel) return;

    const updatedItems = items.map((item: MenuItem) => {
      const updatedItem = { ...item };
      
      if (assign) {
        switch (assignmentLevel.type) {
          case 'concept':
            updatedItem.conceptId = assignmentLevel.id;
            break;
          case 'franchisee':
            updatedItem.franchiseeId = assignmentLevel.id;
            break;
          case 'store':
            updatedItem.storeId = assignmentLevel.id;
            break;
        }
      } else {
        switch (assignmentLevel.type) {
          case 'concept':
            updatedItem.conceptId = '';
            break;
          case 'franchisee':
            updatedItem.franchiseeId = '';
            break;
          case 'store':
            updatedItem.storeId = '';
            break;
        }
      }
      
      return updatedItem;
    });

    setMenuItems(prev => {
      const updated = [...prev];
      updatedItems.forEach(updatedItem => {
        const index = updated.findIndex(mi => mi.menuItemId === updatedItem.menuItemId);
        if (index >= 0) {
          updated[index] = updatedItem;
        }
      });
      return updated;
    });

    setHasChanges(true);
    
    // Clear selections after bulk operation
    setSelectedAvailable(new Set());
    setSelectedAssigned(new Set());
  };

  const handleBulkAssignSelected = (assign: boolean) => {
    const selectedItems = assign 
      ? Array.from(selectedAvailable).map(id => availableItems.find(item => item.menuItemId === id)).filter(Boolean) as MenuItem[]
      : Array.from(selectedAssigned).map(id => assignedItems.find(item => item.menuItemId === id)).filter(Boolean) as MenuItem[];
    
    if (selectedItems.length > 0) {
      handleBulkAssign(selectedItems, assign);
    }
  };

  const saveAssignments = async () => {
    if (!hasChanges) return;

    try {
      setSaving(true);

      const modifiedItems = menuItems.filter(item => {
        const original = originalAssignments.get(item.menuItemId);
        if (!original) return true;
        return (
          (item.conceptId || '') !== original.conceptId ||
          (item.franchiseeId || '') !== original.franchiseeId ||
          (item.storeId || '') !== original.storeId
        );
      });

      if (modifiedItems.length === 0) {
        setHasChanges(false);
        return;
      }

      const results = await Promise.all(
        modifiedItems.map(item =>
          fetch(API_ENDPOINTS.menuItem(item.menuItemId), {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(item)
          })
        )
      );

      const failures = results.filter(r => !r.ok);
      if (failures.length > 0) {
        console.error(`Failed to save ${failures.length} of ${modifiedItems.length} menu item assignments`);
        return;
      }

      setOriginalAssignments(prev => {
        const next = new Map(prev);
        modifiedItems.forEach(item => {
          next.set(item.menuItemId, {
            conceptId: item.conceptId || '',
            franchiseeId: item.franchiseeId || '',
            storeId: item.storeId || ''
          });
        });
        return next;
      });
      setHasChanges(false);
    } catch (error) {
      console.error('Error saving assignments:', error);
    } finally {
      setSaving(false);
    }
  };

  // Filter functions
  const filterItems = (items: MenuItem[], search: string): MenuItem[] => {
    return items.filter(item => {
      const matchesSearch = !search || 
        item.name.toLowerCase().includes(search.toLowerCase()) ||
        item.description.toLowerCase().includes(search.toLowerCase()) ||
        item.category.toLowerCase().includes(search.toLowerCase());
      
      const matchesCategory = !categoryFilter || item.category === categoryFilter;
      
      return matchesSearch && matchesCategory;
    });
  };

  const filteredAvailable = useMemo(() => 
    filterItems(availableItems, searchAvailable), 
    [availableItems, searchAvailable, categoryFilter]
  );

  const filteredAssigned = useMemo(() => 
    filterItems(assignedItems, searchAssigned), 
    [assignedItems, searchAssigned, categoryFilter]
  );

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = Array.from(new Set(menuItems.map((item: MenuItem) => item.category)));
    return cats.sort();
  }, [menuItems]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="mb-6">
        <p className="text-gray-600">
          Assign menu items to concepts, franchisees, and stores. Select items and use the assignment buttons or click individual arrows.
        </p>
        {/* Debug info */}
        <div className="text-xs text-gray-500 mt-2">
          Debug: {enterprises.length} enterprises loaded, selected: {selectedEnterprise || 'none'}
        </div>
      </div>

      {/* Controls */}
      <div className="bg-white rounded-lg shadow-sm border p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
          {/* Enterprise selection lives in the header strip — no in-page picker. */}

          {/* Assignment Level Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Assignment Level
            </label>
            <select
              value={assignmentLevel ? `${assignmentLevel.type}:${assignmentLevel.id}` : ''}
              onChange={(e) => {
                const value = e.target.value;
                if (!value) {
                  setAssignmentLevel(null);
                  return;
                }
                
                const [type, id] = value.split(':');
                let name = '';
                let displayName = '';
                
                switch (type) {
                  case 'concept':
                    const concept = concepts.find(c => c.conceptId === id);
                    name = concept?.name || '';
                    displayName = `Concept: ${name}`;
                    break;
                  case 'franchisee':
                    const franchisee = franchisees.find(f => f.franchiseeId === id);
                    name = franchisee?.name || '';
                    displayName = `Franchisee: ${name}`;
                    break;
                  case 'store':
                    const store = stores.find(s => s.storeId === id);
                    name = store?.name || '';
                    displayName = `Store: ${name}`;
                    break;
                }
                
                setAssignmentLevel({
                  type: type as 'concept' | 'franchisee' | 'store',
                  id,
                  name,
                  displayName
                });
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">Select Assignment Level</option>
              
              <optgroup label="Concepts">
                {concepts.map((concept: Concept) => (
                  <option key={concept.conceptId} value={`concept:${concept.conceptId}`}>
                    {concept.name}
                  </option>
                ))}
              </optgroup>
              
              <optgroup label="Franchisees">
                {franchisees.map((franchisee: Franchisee) => (
                  <option key={franchisee.franchiseeId} value={`franchisee:${franchisee.franchiseeId}`}>
                    {franchisee.name}
                  </option>
                ))}
              </optgroup>
              
              <optgroup label="Stores">
                {stores.map((store: Store) => (
                  <option key={store.storeId} value={`store:${store.storeId}`}>
                    {store.name} {store.storeCode ? `(${store.storeCode})` : ''}
                  </option>
                ))}
              </optgroup>
            </select>
          </div>

          {/* Category Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Category Filter
            </label>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map((category: string) => (
                <option key={category} value={category}>
                  {category}
                </option>
              ))}
            </select>
          </div>

          {/* Save Button */}
          <div className="flex items-end">
            <button
              onClick={saveAssignments}
              disabled={!hasChanges || saving}
              className={`w-full px-4 py-2 rounded-md font-medium flex items-center justify-center space-x-2 ${
                hasChanges && !saving
                  ? 'bg-blue-600 text-white hover:bg-blue-700'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  <span>Save Changes</span>
                </>
              )}
            </button>
          </div>
        </div>

        {hasChanges && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <span className="text-sm text-yellow-800">You have unsaved changes</span>
          </div>
        )}
      </div>

      {/* Assignment Interface */}
      {assignmentLevel && (
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="p-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              Assigning to: {assignmentLevel.displayName}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 divide-x divide-gray-200">
            
            {/* Available Items */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Available Items ({filteredAvailable.length})
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkAssignSelected(true)}
                    disabled={selectedAvailable.size === 0}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Assign Selected ({selectedAvailable.size})</span>
                  </button>
                  <button
                    onClick={() => handleBulkAssign(filteredAvailable, true)}
                    disabled={filteredAvailable.length === 0}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-md hover:bg-blue-200 disabled:opacity-50"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Assign All</span>
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search available items..."
                    value={searchAvailable}
                    onChange={(e) => setSearchAvailable(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredAvailable.map((item: MenuItem) => (
                  <MenuItemCard
                    key={item.menuItemId}
                    item={item}
                    isAssigned={false}
                    isSelected={selectedAvailable.has(item.menuItemId)}
                    onSelect={() => {
                      const newSelected = new Set(selectedAvailable);
                      if (newSelected.has(item.menuItemId)) {
                        newSelected.delete(item.menuItemId);
                      } else {
                        newSelected.add(item.menuItemId);
                      }
                      setSelectedAvailable(newSelected);
                    }}
                    onToggle={() => toggleItemAssignment(item, true)}
                  />
                ))}
                
                {filteredAvailable.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No available items to assign
                  </div>
                )}
              </div>
            </div>

            {/* Assigned Items */}
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Assigned Items ({filteredAssigned.length})
                </h3>
                <div className="flex space-x-2">
                  <button
                    onClick={() => handleBulkAssignSelected(false)}
                    disabled={selectedAssigned.size === 0}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50"
                  >
                    <Minus className="h-4 w-4" />
                    <span>Remove Selected ({selectedAssigned.size})</span>
                  </button>
                  <button
                    onClick={() => handleBulkAssign(filteredAssigned, false)}
                    disabled={filteredAssigned.length === 0}
                    className="flex items-center space-x-1 px-3 py-1 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200 disabled:opacity-50"
                  >
                    <Minus className="h-4 w-4" />
                    <span>Remove All</span>
                  </button>
                </div>
              </div>

              <div className="mb-4">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search assigned items..."
                    value={searchAssigned}
                    onChange={(e) => setSearchAssigned(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredAssigned.map((item: MenuItem) => (
                  <MenuItemCard
                    key={item.menuItemId}
                    item={item}
                    isAssigned={true}
                    isSelected={selectedAssigned.has(item.menuItemId)}
                    onSelect={() => {
                      const newSelected = new Set(selectedAssigned);
                      if (newSelected.has(item.menuItemId)) {
                        newSelected.delete(item.menuItemId);
                      } else {
                        newSelected.add(item.menuItemId);
                      }
                      setSelectedAssigned(newSelected);
                    }}
                    onToggle={() => toggleItemAssignment(item, false)}
                  />
                ))}
                
                {filteredAssigned.length === 0 && (
                  <div className="text-center py-12 text-gray-500">
                    No items assigned yet
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {!assignmentLevel && (
        <div className="bg-white rounded-lg shadow-sm border p-12 text-center">
          <Filter className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            Select Assignment Level
          </h3>
          <p className="text-gray-600">
            Choose a concept, franchisee, or store to begin assigning menu items.
          </p>
        </div>
      )}
    </div>
  );
};

export default MenuItemAssignment;